import { normalizeActiveProgram } from '../utils/programEnrollment';
import { defaultExercises } from '../data/exercises';
import { convertStateUnits } from '../utils/units';

export const defaultSchedule = {
  weekly:{0:"Rest",1:"Chest",2:"Back",3:"Legs",4:"Shoulders",5:"Arms",6:"Cardio"},
  overrides:{}
};
export const init={tab:"home",workouts:[],nutrition:[],body:[],exercises:defaultExercises,
  goals:{cal:2400,protein:180,carbs:250,fat:70,goalWeight:175},schedule:defaultSchedule,
  range:30,loaded:false,modal:null,units:"lbs",onboarded:false,photos:[],checkins:[],milestones:[],
  phases:[],injuries:[],activeProgram:null,
  // Tombstones. Deleting a row only ever filtered it out of the array, and a push sends
  // the arrays — so the server never learned. Its copy stayed deleted:false forever:
  // friends kept seeing the workout in friend_workouts, the gym's coaches kept seeing it
  // in client_detail, and a restore on a new device brought it back. Absence cannot mean
  // deletion, because a push can legitimately omit a domain (syncPrefs) or drop oversized
  // photos, so the ids are carried explicitly and cleared once the server confirms.
  deletions:{workouts:[],nutrition:[],body:[],photos:[],checkins:[]},
  syncPrefs:{workouts:true,nutrition:true,body:true,photos:true,checkins:true,milestones:true},
  a11y:{largeText:false,highContrast:false,reduceMotion:false},
  profile:{firstName:"",lastName:"",nickname:"",email:"",dob:"",sex:"",state:"",
    height:"",fitnessLevel:"",activityLevel:"",weeklyAvailability:""}};

// Allow-list of state keys an external import is permitted to set. Anything else
// is dropped so a malformed file can't inject e.g. `tab:"admin"` or arbitrary keys.
const IMPORTABLE_KEYS = new Set([
  "workouts","nutrition","body","exercises","goals","schedule","units","photos","checkins",
  "milestones","phases","injuries","activeProgram","syncPrefs","a11y","profile","range",
]);

// Validate-and-filter an import payload. Returns a clean partial state.
const _sanitizeImport = (raw) => {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const out = {};
  for (const k of Object.keys(raw)) {
    if (!IMPORTABLE_KEYS.has(k)) continue;
    const v = raw[k];
    // Array-shaped keys
    if (["workouts","nutrition","body","exercises","photos","checkins","milestones","phases","injuries"].includes(k)) {
      if (Array.isArray(v)) out[k] = v;
      continue;
    }
    // Object-shaped keys
    if (["goals","schedule","syncPrefs","a11y","profile"].includes(k)) {
      if (v && typeof v === "object" && !Array.isArray(v)) out[k] = v;
      continue;
    }
    if (k === "activeProgram") out[k] = normalizeActiveProgram(v);
    // Scalars
    if (k === "units" && (v === "lbs" || v === "kg")) out[k] = v;
    if (k === "range" && Number.isFinite(+v)) out[k] = +v;
  }
  return out;
};

// Add one id to the tombstone list for `kind`, capped so a long-lived install cannot
// grow the push payload without bound. Duplicates are ignored.
const _tomb=(s,kind,id)=>{
  if(id===undefined||id===null)return s.deletions||init.deletions;
  const cur=(s.deletions&&s.deletions[kind])||[];
  if(cur.includes(id))return s.deletions;
  return {...(s.deletions||init.deletions),[kind]:[...cur,id].slice(-500)};
};

export function reducer(s,a){
  switch(a.type){
    case "INIT": return{...s,...a.p,loaded:true};
    case "TAB": return{...s,tab:a.tab};
    case "RANGE": return{...s,range:a.d};
    case "ADD_W": return{...s,workouts:[a.w,...s.workouts.filter(w=>w.id!==a.w.id)].sort((a,b)=>b.date.localeCompare(a.date))};
    case "EDIT_W": return{...s,workouts:s.workouts.map(w=>w.id===a.w.id?{...a.w}:w).sort((x,y)=>(y.date||"").localeCompare(x.date||""))};
    case "DEL_W": return{...s,workouts:s.workouts.filter(w=>w.id!==a.id),deletions:_tomb(s,"workouts",a.id)};
    // ADD_N: dedup by id AND date — date is the natural "one log per day" key but id
    // is canonical when merging cloud snapshots that already include same-id rows.
    case "ADD_N": return{...s,nutrition:[a.n,...s.nutrition.filter(n=>n.id!==a.n.id && n.date!==a.n.date)].sort((a,b)=>b.date.localeCompare(a.date))};
    // MERGE_N: a new entry for a day that already has one adds to that day instead of
    // replacing it (ADD_N's date dedupe silently threw the earlier log away).
    case "MERGE_N": {
      const cur=s.nutrition.find(n=>n.date===a.n.date&&n.id!==a.n.id);
      if(!cur)return reducer(s,{type:"ADD_N",n:a.n});
      const sum=k=>(Number(cur[k])||0)+(Number(a.n[k])||0);
      const meals=(cur.meals||[]).map(m=>({...m,items:[...(m.items||[])]}));
      (a.n.meals||[]).forEach(m=>{
        const i=meals.findIndex(x=>x.name===m.name);
        if(i<0)meals.push({...m,items:[...(m.items||[])]});
        else meals[i]={...meals[i],time:meals[i].time||m.time,items:[...meals[i].items,...(m.items||[])]};
      });
      const merged={...cur,cal:sum("cal"),protein:sum("protein"),carbs:sum("carbs"),fat:sum("fat"),fiber:sum("fiber"),
        sugar:sum("sugar"),sodium:sum("sodium"),water:Math.max(cur.water||0,a.n.water||0),sleep:a.n.sleep||cur.sleep||0,meals};
      return{...s,nutrition:s.nutrition.map(n=>n.id===cur.id?merged:n)};
    }
    case "EDIT_N": return{...s,nutrition:s.nutrition.map(n=>n.id===a.n.id?{...a.n}:n).sort((x,y)=>(y.date||"").localeCompare(x.date||""))};
    case "DEL_N": return{...s,nutrition:s.nutrition.filter(n=>n.id!==a.id),deletions:_tomb(s,"nutrition",a.id)};
    case "ADD_B": return{...s,body:[a.b,...s.body.filter(b=>b.id!==a.b.id)].sort((a,b)=>b.date.localeCompare(a.date))};
    case "EDIT_B": return{...s,body:s.body.map(b=>b.id===a.b.id?{...a.b}:b).sort((x,y)=>(y.date||"").localeCompare(x.date||""))};
    case "DEL_B": return{...s,body:s.body.filter(b=>b.id!==a.id),deletions:_tomb(s,"body",a.id)};
    case "GOALS": return{...s,goals:{...s.goals,...a.g}};
    // Guard against duplicate exercise IDs (breaks lookups / PR aggregation otherwise)
    // `replace` refreshes an existing entry (a gym exercise the coach renamed); otherwise an id already present is left alone
    case "ADD_EX": {
      const i=s.exercises.findIndex(e=>e.id===a.ex.id);
      if(i<0)return{...s,exercises:[...s.exercises,a.ex]};
      if(!a.replace)return s;
      const exercises=[...s.exercises];exercises[i]={...exercises[i],...a.ex};
      return{...s,exercises};
    }
    case "SET_ACTIVE_PROGRAM": return{...s,activeProgram:normalizeActiveProgram(a.activeProgram)};
    case "SET_WEEKLY": return{...s,schedule:{...s.schedule,weekly:{...s.schedule.weekly,[a.day]:a.label}}};
    case "SET_OVERRIDE": {
      const ov={...s.schedule.overrides};
      if(a.label)ov[a.date]=a.label; else delete ov[a.date];
      return{...s,schedule:{...s.schedule,overrides:ov}};
    }
    // Merge rather than replace so callers passing a partial don't wipe siblings
    case "SET_SCHEDULE": {
      const incoming = (a.schedule && typeof a.schedule==="object") ? a.schedule : {};
      return{...s,schedule:{
        weekly: incoming.weekly && typeof incoming.weekly==="object" ? incoming.weekly : s.schedule.weekly,
        overrides: incoming.overrides && typeof incoming.overrides==="object" ? incoming.overrides : s.schedule.overrides,
      }};
    }
    // Raw unit setter — only for signup / cloud restore, where the data already matches the unit
    case "UNITS": return{...s,units:a.u};
    // User toggled units: convert every stored weight / distance / tape value along with the label
    case "CONVERT_UNITS": {
      if(!a.to||a.to===s.units)return s;
      return{...s,...convertStateUnits(s,s.units,a.to),units:a.to};
    }
    case "ONBOARDED": return{...s,onboarded:true};
    case "SET_PROFILE": return{...s,profile:{...(s.profile||{}),...a.profile}};
    case "ADD_CHECKIN": return{...s,checkins:[a.c,...(s.checkins||[]).filter(c=>c.id!==a.c.id && c.date!==a.c.date)]};
    case "SET_MILESTONES": return{...s,milestones:a.milestones||[]};
    case "SET_PHASES": return{...s,phases:a.phases||[]};
    case "SET_INJURIES": return{...s,injuries:a.injuries||[]};
    case "SET_SYNC_PREFS": return{...s,syncPrefs:{...(s.syncPrefs||{}),...a.prefs}};
    case "SET_A11Y": return{...s,a11y:{...(s.a11y||{}),...a.a11y}};
    case "ADD_PHOTO": return{...s,photos:[a.photo,...s.photos.filter(p=>p.id!==a.photo.id)]};
    // Only flip synced:true when the upload actually returned a URL — otherwise
    // a failed upload would mark a still-local photo as synced and lose it on next pull.
    // The URL itself is NEVER stored: it is a signed link that expires within the hour and
    // carries the storage key, and this row is synced to the server. The photo id stays the
    // durable handle (plus the storage `path` when the API returns one) and the render URL is
    // resolved at display time — see utils/photoUrls.js.
    case "UPDATE_PHOTO": return{...s,photos:s.photos.map(p=>p.id===a.id?{...p,...(a.url?{data:"",remote:true,path:a.path||p.path||null,synced:true}:{})}:p)};
    // The server has applied the tombstones in `a.sent`; stop resending those. Ids deleted
    // after that payload was built are kept so they go out on the next push.
    case "DELETIONS_SYNCED": {
      const cur=s.deletions||init.deletions;const sent=a.sent||{};const next={...init.deletions};
      Object.keys({...init.deletions,...cur}).forEach(k=>{const done=new Set(sent[k]||[]);next[k]=(cur[k]||[]).filter(id=>!done.has(id));});
      return{...s,deletions:next};
    }
    case "DEL_PHOTO": return{...s,photos:s.photos.filter(p=>p.id!==a.id),deletions:_tomb(s,"photos",a.id)};
    // CLEAR_ALL: wipe data but keep units, a11y, and profile.email so SSO can re-pull
    // intentionally. Set onboarded:false so the Onboarding screen reappears.
    case "CLEAR_ALL": return{
      ...init,
      exercises:defaultExercises,
      loaded:true,
      onboarded:false,
      units:s.units,
      a11y:s.a11y,
      profile:{...init.profile, email: s.profile?.email || ""},
    };
    // Full logout: clear everything except a11y so the next user starts fresh
    case "LOGOUT": return{
      ...init,
      exercises:defaultExercises,
      loaded:true,
      onboarded:false,
      a11y:s.a11y,
    };
    // Validated import — drop unknown keys, normalize shapes
    case "IMPORT": return{...s, ..._sanitizeImport(a.data), loaded:true};
    case "XP_REFRESH": return{...s,_xpTs:Date.now()}; // nudge re-render so IronScore reads fresh LS
    default: return s;
  }
}
